<template lang="pug">
AppMapMarker.activities-location-avatar-marker(
  :marker="marker"
  has-icon
  has-tooltip
  is-clickable
  has-info-window
  :z-index="zIndex"
  :icon-size="size"
  centered-on-coordinates
  :icon-url="getAvatarUrl"
  :circle-options="circleOptions"
  :show-marker-radius="showRadius"
  :show-info-window="showInfoWindow"
  :info-window-options="infoWindowOptions"
  @click="$emit('click', $event)"
  @close-info="$emit('close-info', $event)"
)
  //- View info marker
  GmapMarker(
    ref="viewInfoMarker"
    clickable
    :visible="selected"
    :z-index="zIndex"
    :draggable="false"
    :icon="viewInfoIcon"
    :position="viewInfoMarkerPosition"
    @click="toggleInfoWindow"
    @mouseover="onHoverSideMarker('info')"
    @mouseout="onHoverSideMarker(null)"
  )
  //- View route marker
  GmapMarker(
    ref="routeMarker"
    clickable
    :visible="selected"
    :z-index="zIndex"
    :draggable="false"
    :icon="viewRouteIcon"
    :position="viewRouteMarkerPosition"
    @mouseout="onHoverSideMarker(null)"
    @mouseover="onHoverSideMarker('route')"
    @click="toggleRoute"
  )
  template(slot="infoWindow")
    ActivitiesLocationMemberInfoWindow(
      v-if="showInfoWindow"
      :marker="marker"
      :active="showInfoWindow"
      :active-route="activeRoute"
      @click="$emit('select-site', $event)"
      @close="$emit('close-info', $event)"
      @view-route="toggleRoute"
    )
  template(slot="tooltip")
    | {{ marker.name }}

  //- Side marker's tooltip
  GmapInfoWindow(
    ref="sideTooltip"
    :opened="showTooltip"
    :position="sideTooltipPosition"
    :options="{ pixelOffset: { width: 0, height: -15 } }"
  )
    div(:class="`app-map-side-marker-tooltip-${marker.id}`")
      | {{ sideMarkerTooltipText }}
</template>

<script>
import { get } from 'lodash-es';
import { activitiesMarker } from '~/mixins';
import getIconData from '../../utils/get_icon_data';
import { ACTIVITIES_AVATAR_SIDE_MARKERS } from '~/app_constants';

const AVATAR_RADIUS = 50;
const SIDE_ICONS_SIZE = 30;

export default {
  name: 'ActivitiesLocationAvatarMarker',
  mixins: [activitiesMarker({ zIndex: 3 })],
  props: {
    selected: Boolean,
    activeRoute: Boolean,
    map: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    showTooltip: false,
    sideMarkerTooltip: null,
    sideIconSize: Object.freeze({
      width: SIDE_ICONS_SIZE,
      height: SIDE_ICONS_SIZE,
    }),
  }),
  computed: {
    circleOptions() {
      return {
        ...this.defaultCircleOptions,
        radius: get(this.marker, 'accuracy', AVATAR_RADIUS),
      };
    },
    infoWindowOptions: () => ({ pixelOffset: { width: 0, height: -30 } }),
    getAvatarUrl() {
      return get(this.marker, 'avatar.thumb') + '#avatar';
    },
    viewInfoIcon() {
      const avatarKey = this.showInfoWindow ? 'active' : 'inactive';
      const iconUrl = ACTIVITIES_AVATAR_SIDE_MARKERS.viewInfo[avatarKey];

      return getIconData(this.sideMarkersIconSize, iconUrl);
    },
    viewRouteIcon() {
      const avatarKey = this.activeRoute ? 'active' : 'inactive';
      const iconUrl = ACTIVITIES_AVATAR_SIDE_MARKERS.viewRoute[avatarKey];

      return getIconData(this.sideMarkersIconSize, iconUrl);
    },
    sideMarkersIconSize() {
      if (!this.map) {
        return this.sideIconSize;
      }

      const mapZoom = this.map.getZoom();
      const zoomLevel = Math.min(Math.max(mapZoom, 9), 15);
      const ratio = parseFloat(zoomLevel / 15);
      const width = SIDE_ICONS_SIZE * ratio;

      return {
        width,
        height: width,
      };
    },
    showViewInfoMarker() {
      return this.showRadius && !!this.viewInfoMarkerPosition;
    },
    viewInfoMarkerPosition() {
      return this.calculateSideMarkerPosition('left');
    },
    viewRouteMarkerPosition() {
      return this.calculateSideMarkerPosition('right');
    },
    showViewRouteMarker() {
      return this.showRadius && !!this.viewRouteMarkerPosition;
    },
    sideTooltipPosition() {
      return this.showTooltip
        ? this.sideMarkerTooltip === 'route'
          ? this.viewRouteMarkerPosition
          : this.viewInfoMarkerPosition
        : null;
    },
    sideMarkerTooltipText() {
      if (!this.sideMarkerTooltip) {
        return null;
      }

      const isActiveRoute = this.activeRoute && this.sideMarkerTooltip === 'route';
      const isActiveInfo = this.showInfoWindow && this.sideMarkerTooltip === 'info';
      const activeTooltip = isActiveInfo || isActiveRoute;

      return `${activeTooltip ? 'Hide' : 'View'} ${this.sideMarkerTooltip}`;
    },
  },
  watch: {
    async showTooltip(newValue) {
      if (!newValue) {
        return;
      }

      await this.$nextTick();

      const mapDiv = this.map.getDiv();

      if (!mapDiv || !mapDiv.querySelector) {
        return;
      }

      const markerInfoWindow = mapDiv.querySelector(`.app-map-side-marker-tooltip-${this.marker.id}`);
      const iw = markerInfoWindow.parentNode.parentNode.parentNode;

      iw.parentNode.parentNode.classList.add('tooltip-style-iw-a');
    },
  },
  methods: {
    toggleRoute() {
      const routeEvent = this.activeRoute ? 'close-route' : 'view-route';
      const payload = this.activeRoute ? null : this.marker.id;

      this.$emit(routeEvent, payload);
    },
    toggleInfoWindow() {
      const infoWindowEvent = this.showInfoWindow ? 'close-info' : 'info-window';
      const payload = this.showInfoWindow ? null : this.marker.id;

      this.$emit(infoWindowEvent, payload);
    },
    onHoverSideMarker(sideMarker) {
      this.showTooltip = !!sideMarker;
      this.sideMarkerTooltip = sideMarker;
    },
    /**
     * Method in charge of calculating a pixel offset in
     * the map, based on a coordinate position.
     */
    calculateSideMarkerPosition(side) {
      const map = this.map;

      if (!map || !map.getProjection()) {
        return;
      }

      const { width: sideWidth } = this.sideMarkersIconSize;
      const { width: avatarWidth } = this.size;
      const lat = get(this.marker, 'position.lat');
      const lng = get(this.marker, 'position.lng');
      // eslint-disable-next-line no-undef
      const { Point } = google.maps;
      const scale = Math.pow(2, map.getZoom());
      // eslint-disable-next-line no-undef
      const centerPointCoordinates = new google.maps.LatLng(lat, lng);
      const centerPoint = map.getProjection().fromLatLngToPoint(centerPointCoordinates);
      const offsetY = 0;
      const offsetX = (avatarWidth / 2 + 18 + sideWidth / 2) * (side === 'right' ? -1 : 1);
      const pointOffset = new Point(offsetX / scale || 0, offsetY);
      const newMarkerCenter = new Point(centerPoint.x - pointOffset.x, centerPoint.y - pointOffset.y);

      return map.getProjection().fromPointToLatLng(newMarkerCenter);
    },
  },
};
</script>
